import {Directive, TemplateRef, ViewContainerRef, OnDestroy, OnInit, Input} from "@angular/core";
import {AppSettingsService} from "./app-settings.service";
import {Subscription} from "rxjs";
import {AuthDetailModel} from "./auth-detail.model";

export type PrivilegeInputType = string|(string|string[])[];

/**
 * This class toggles child element based on the user's privileges.
 * It subscribes to AppSettingsService.
 * Based on [ngIf]: https://github.com/angular/angular/blob/2.2.x/modules/%40angular/common/src/directives/ng_if.ts
 *
 * Input type is a string, or array of strings, or 2D array of strings, or some mixture.
 * Any items past 2 levels deep of strings will be coerced into strings.
 * Inputs that are null or undefined will be coerced into "".
 *
 * Examples:
 *
 * <div *privilege="'foo'"> visible for any 'foo' </div>
 * <div *privilege="['foo']"> visible for any 'foo' </div>
 * <div *privilege="[['foo']]"> visible for any 'foo' </div>
 * <div *privilege="['foo', 'bar']"> visible for any 'foo' or any 'bar' </div>
 * <div *privilege="[['foo'], 'bar']"> visible for any 'foo' or any 'bar' </div>
 * <div *privilege="[['foo'], ['bar']]"> visible for any 'foo' or any 'bar' </div>
 * <div *privilege="[['foo', 'bar']]"> visible for any who is both 'foo' and 'bar' </div>
 * <div *privilege="[['foo', 'bar'], 'baz']"> visible for any who is both 'foo' and 'bar', or 'baz' </div>
 * <div *privilege="[['foo', 'bar'], ['baz']]"> visible for any who is both 'foo' and 'bar', or 'baz' </div>
 */
@Directive({
  selector: "[privilege]",
})
export class PrivilegeDirective implements OnInit, OnDestroy {
  private inputPrivileges: PrivilegeInputType;
  private privileges: string[][] = [];
  private subscription: Subscription;
  private hasView: boolean = false;
  private user: AuthDetailModel;

  private update(user?: AuthDetailModel) {
    if (user) this.user = user;

    if (!this.user) return;
    if (!this.privileges) return;

    let condition = false;

    this.privileges.forEach((items) => {
      condition = condition || items.every(item => this.user.privilegeFlags[item]);
    });

    if (condition && !this.hasView) {
      this.hasView = true;
      this.viewContainer.createEmbeddedView(this.template);
    } else if (!condition && this.hasView) {
      this.hasView = false;
      this.viewContainer.clear();
    }
  }

  @Input()
  public set privilege(v: PrivilegeInputType) {
    this.inputPrivileges = v;

    if (v === null || v === undefined) v = "";
    if (typeof v === "string") v = [[`${v}`]];
    if (!Array.isArray(v)) throw new Error("Expected string|Array<string|Array<string>> for [privilege]");

    this.privileges = Array.prototype.map.call(v, (item) => {
      if (item === null || item === undefined) return [""];
      if (typeof item === "string") return [`${item}`];
      if (!Array.isArray(item)) throw new Error("Expected string|Array<string|Array<string>> for [privilege]");
      return Array.prototype.map.call(item, (item2) => {
        if (item2 === null || item2 === undefined) return "";
        return `${item2}`;
      });
    });

    this.update();
  }

  public get privilege(): PrivilegeInputType {
    return this.inputPrivileges;
  }

  ngOnInit(): void {
    if (this.subscription) return;
    this.subscription = this.appSettingsService.user.subscribe(user => this.update(user));
  }

  ngOnDestroy(): void {
    if (!this.subscription || this.subscription.closed) return;
    this.subscription.unsubscribe();
    this.subscription = undefined;
  }

  constructor(
    private template: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private appSettingsService: AppSettingsService,
  ) {}
}
